import { getLanguages } from "api/languages";
import { ReactComponent as EmailIcon } from "assets/icons/email.svg";
import { ReactComponent as FaxIcon } from "assets/icons/fax.svg";
import { ReactComponent as LinkedInIcon } from "assets/icons/linked-in.svg";
import { ReactComponent as PhoneIcon } from "assets/icons/phone.svg";
import { ReactComponent as YoutubeIcon } from "assets/icons/youtube.svg";
import { ReactComponent as FacebookIcon } from "assets/icons/facebook.svg";
import { ReactComponent as InstagramIcon } from "assets/icons/instagram.svg";
import Link from "components/Link";
import Responsive from "components/Responsive";
import {
  ACIN,
  COMPLAINTS_BOOK,
  IPARQUE_DRIVER,
  IPARQUE_FACEBOOK,
  IPARQUE_INSTAGRAM,
  IPARQUE_LINKEDIN,
  IPARQUE_ONSTREET,
  IPARQUE_YOUTUBE,
} from "configs/links";
import { langs, translateSegmentsAuto } from "helpers/lang";
import useLocalizedRoutes from "hooks/useLocalizedRoutes";
import { Language, LanguageOption } from "interfaces/locale";
import { AcinSolution, Text } from "iparque-components";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { breakpoints } from "theme/breakpoints";
import {
  ColumnRightWrapper,
  Container,
  IconLabelContainer,
  SocialIconsWrapper,
  StyledColumn,
  StyledLogo,
  StyledSelect,
  Ul,
} from "./styles";

interface Props {
  modulesURL?: string;
}

const MenuAndContacts: React.FC<Props> = ({ modulesURL = "" }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [languageOptions, setLanguageOptions] = useState<Array<LanguageOption>>(
    []
  );
  const { getExternalLanguageRoute } = useLocalizedRoutes();

  const currentLanguage = useMemo(
    () => i18n.language.substring(0, 2) as Language,
    [i18n.language]
  );

  useEffect(() => {
    (async () => {
      setLanguageOptions(await getLanguages(currentLanguage));
    })();
  }, [currentLanguage, t]);

  const onChangeLanguage = (event: ChangeEvent<HTMLSelectElement>) => {
    const lang = (event.target.value || langs.pt) as Language;
    const previousLang = (i18n.language ?? langs.pt) as Language;
    i18n.changeLanguage(lang);
    const next = translateSegmentsAuto(pathname, previousLang, lang);
    navigate(next);
  };

  return (
    <Container>
      <StyledColumn>
        <Link
          rel="noopener"
          target="_blank"
          href={getExternalLanguageRoute("landingPage")}
        >
          <StyledLogo />
        </Link>
        <SocialIconsWrapper>
          <Link ariaLabel="Facebook" href={IPARQUE_FACEBOOK}>
            <FacebookIcon
              role="img"
              aria-labelledby={t("10657") /* Facebook logo */}
              title={t("10657") /* Facebook logo */}
            />
          </Link>
          <Link ariaLabel="Instagram" href={IPARQUE_INSTAGRAM}>
            <InstagramIcon
              role="img"
              aria-labelledby={t("10658") /* Instagram logo */}
              title={t("10658") /* Instagram logo */}
            />
          </Link>
          <Link ariaLabel="LinkedIn" href={IPARQUE_LINKEDIN}>
            <LinkedInIcon
              role="img"
              aria-labelledby={t("10659") /* Linkedin logo */}
              title={t("10659") /* Linkedin logo */}
            />
          </Link>
          <Link ariaLabel="Youtube" href={IPARQUE_YOUTUBE}>
            <YoutubeIcon
              role="img"
              aria-labelledby={t("10660") /* Youtube logo */}
              title={t("10660") /* Youtube logo */}
            />
          </Link>
        </SocialIconsWrapper>
        <Responsive minWidth={breakpoints.md}>
          <AcinSolution translation={t("10551") /* Uma solução */} />
        </Responsive>
      </StyledColumn>
      <StyledColumn center className="hide-on-mobile-devices">
        <nav aria-label="Menu principal">
          <Text
            className="footer-column-title mb-10"
            variant="body6"
            color="tertiary"
            as="h2"
          >
            {t("7818") /* Menu */}
          </Text>
          <Ul>
            <li>
              <Text variant="body7" color="quaternary">
                <Link
                  rel="noopener"
                  target="_blank"
                  href={getExternalLanguageRoute("landingPage")}
                >
                  {t("5873") /* iParque */}
                </Link>
              </Text>
            </li>
            <li>
              <Text variant="body7" color="quaternary">
                <Link href={`${modulesURL}#modules`} target="_self">
                  {t("7793") /* Módulos */}
                </Link>
              </Text>
            </li>
            <li>
              <Text variant="body7" color="quaternary">
                <Link href={IPARQUE_DRIVER} target="_self">
                  iParque Driver
                </Link>
              </Text>
            </li>
            <li>
              <Text variant="body7" color="quaternary">
                <Link href={IPARQUE_ONSTREET} target="_self">
                  iParque OnStreet
                </Link>
              </Text>
            </li>
            <li>
              <Text variant="body7" color="quaternary">
                <Link
                  underline="always"
                  target="_blank"
                  href={getExternalLanguageRoute("certificates")}
                  rel="noopener"
                >
                  {t("2261") /* Certificações */}
                </Link>
              </Text>
            </li>
            <li>
              <Text variant="body7" color="quaternary">
                <Link href={ACIN}>ACIN Group</Link>
              </Text>
            </li>
            <li>
              <Text variant="body7" color="quaternary">
                <Link href={COMPLAINTS_BOOK}>
                  {t("7209") /* Elogios, Sugestões e Reclamações */}
                </Link>
              </Text>
            </li>
          </Ul>
        </nav>
      </StyledColumn>
      <StyledColumn>
        <ColumnRightWrapper>
          <div>
            <Text
              variant="body6"
              color="tertiary"
              className="hide-on-mobile-devices mb-10"
            >
              {t("10970") /* Fale connosco */}
            </Text>
            <ContactsText variant="body7" color="quaternary">
              {
                t(
                  "11609"
                ) /* Estamos ao seu dispor, nos dias úteis, das 9h00 às 18h00. */
              }
            </ContactsText>
            <IconLabelContainer>
              <PhoneIcon
                className="footer-icon"
                role="img"
                aria-labelledby={t("10662") /* Icon telefone */}
                title={t("10662") /* Icon telefone */}
              />
              <Link href="tel:707 451 451">
                <Text variant="body7" color="quaternary">
                  707 451 451
                </Text>
              </Link>
            </IconLabelContainer>
            <CallCostText variant="body9" color="quaternary">
              {
                t(
                  "11495"
                ) /* Preço máximo a pagar por minuto: 0,09€ (+IVA) para as chamadas originadas nas redes fixas e 0,13€ (+IVA) para as originadas nas redes móveis. */
              }
            </CallCostText>
            <IconLabelContainer>
              <EmailIcon
                className="footer-icon"
                role="img"
                aria-labelledby={t("10663") /* Icon email */}
                title={t("10663") /* Icon email */}
              />
              <Link href="mailto:apoio@iparque.pt">
                <Text variant="caption1" color="quaternary">
                  apoio@iparque.pt
                </Text>
              </Link>
            </IconLabelContainer>
            <IconLabelContainer>
              <FaxIcon
                className="footer-icon"
                role="img"
                aria-labelledby={t("10664") /* Icon fax */}
                title={t("10664") /* Icon fax */}
              />
              <Text variant="caption1" color="quaternary">
                +351 291 957 171
              </Text>
            </IconLabelContainer>
            <StyledSelect
              name="language"
              value={currentLanguage}
              onChange={onChangeLanguage}
              options={languageOptions}
              className="mt-20"
            />
          </div>
        </ColumnRightWrapper>
      </StyledColumn>
    </Container>
  );
};

const CallCostText = styled(Text)`
  max-width: 250px;
  line-height: 1.5em;
  margin-left: 20px;
  margin-bottom: 5px;

  @media (max-width: ${breakpoints.md}) {
    max-width: 260px;
  }
`;

const ContactsText = styled(Text)`
  max-width: 250px;
  line-height: 1.5em;
  margin-bottom: 20px;
`;

export default MenuAndContacts;
